import React, { createContext, useContext, useRef, useCallback, useMemo } from 'react';

interface PageRefreshContextType {
  registerRefreshHandler: (path: string, handler: () => void) => void;
  unregisterRefreshHandler: (path: string) => void;
  triggerRefresh: (path: string) => void;
}

const PageRefreshContext = createContext<PageRefreshContextType | undefined>(undefined);

export function PageRefreshProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const refreshHandlersRef = useRef<Map<string, () => void>>(new Map());

  const registerRefreshHandler = useCallback((path: string, handler: () => void) => {
    refreshHandlersRef.current.set(path, handler);
  }, []);

  const unregisterRefreshHandler = useCallback((path: string) => {
    refreshHandlersRef.current.delete(path);
  }, []);

  const triggerRefresh = useCallback((path: string) => {
    const handler = refreshHandlersRef.current.get(path);
    if (handler) {
      console.log('🔄 Triggering refresh for path:', path);
      handler();
    } else {
      console.log('⚠️ No refresh handler registered for path:', path);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ registerRefreshHandler, unregisterRefreshHandler, triggerRefresh }),
    [registerRefreshHandler, unregisterRefreshHandler, triggerRefresh]
  );

  return (
    <PageRefreshContext.Provider value={contextValue}>
      {children}
    </PageRefreshContext.Provider>
  );
}

export function usePageRefresh() {
  const context = useContext(PageRefreshContext);
  if (!context) {
    throw new Error('usePageRefresh must be used within a PageRefreshProvider');
  }
  return context;
}

/**
 * Hook to register a refresh handler for the current page
 * @param refreshHandler - Function to call when the page needs to refresh
 * @param path - Optional path to register for (defaults to current location)
 */
export function useRegisterPageRefresh(refreshHandler: () => void, path?: string) {
  const { registerRefreshHandler, unregisterRefreshHandler } = usePageRefresh();
  const currentPath = path ?? window.location.pathname;

  React.useEffect(() => {
    registerRefreshHandler(currentPath, refreshHandler);
    return () => {
      unregisterRefreshHandler(currentPath);
    };
  }, [currentPath, refreshHandler, registerRefreshHandler, unregisterRefreshHandler]);
}
